import { Stack } from '@mui/material';
import { compact } from 'lodash';
import { useWatch } from 'react-hook-form';

import { Accordion } from '@/components/Accordion/Accordion';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwareDatePickerInput } from '@/components/form/formAwareInputs/FormAwareDatePickerInput';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useFormContext } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { GSTExemptionAdjustment } from '@/modules/content/tooltipContent/GSTExemptionAdjustment';
import { LifetimeExemptionAdjustment } from '@/modules/content/tooltipContent/LifetimeExemptionAdjustment';
import { MultiDocumentUploaderWithListV2 } from '@/modules/documents/MultiDocumentUploader/MultiDocumentUploaderWithListV2';
import { PrimaryClientDropdownSingle } from '@/modules/entities/inputs/PrimaryClientDropdown/PrimaryClientDropdown';
import { getPrimaryClientOptionsFromData } from '@/modules/entities/inputs/PrimaryClientDropdown/PrimaryClientDropdown.utils';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { StateCode } from '@/types/schema';

import { assetTypeOptions } from '../LogNewGiftModal/LogNewGiftModal.constants';
import { useRecordExemptionUsedModalFormOptionsQuery } from './graphql/RecordExemptionUsedModal.generated';
import { NAMESPACE } from './RecordExemptionUsedModal.constants';
import {
  RecordExemptionUsedForm,
  RecordExemptionUsedFormPaths,
} from './RecordExemptionUsedModal.types';

export function useFormOptions({ householdId }: { householdId: string }) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { control } = useFormContext<RecordExemptionUsedForm>();
  const { primaryClients } = useHouseholdDetailsContext();

  const { data } = useRecordExemptionUsedModalFormOptionsQuery({
    variables: {
      where: {
        id: householdId,
      },
    },
    onError: (error) => {
      showFeedback(
        "We weren't able to load this form. Please refresh the page and try again."
      );
      reportError(
        'failed to load form options for the LogNewGiftModal',
        error,
        {
          householdId,
        }
      );
    },
  });

  const selectedGrantor = useWatch({
    name: `${NAMESPACE}.grantor` as const satisfies RecordExemptionUsedFormPaths,
    control,
  });

  const household = data?.households.edges?.[0]?.node ?? null;
  const primaryClientOptions = getPrimaryClientOptionsFromData(
    household,
    compact([selectedGrantor.clientProfileId])
  );

  const hasCtResident =
    primaryClients?.some((client) => client.stateCode === StateCode.Ct) ??
    false;

  return {
    primaryClientOptions,
    hasCtResident,
  };
}

interface Props {
  primaryClientOptions: ReturnType<
    typeof useFormOptions
  >['primaryClientOptions'];
  hasCtResident: boolean;
  householdId: string;
}

export function RecordExemptionUsedModalForm({
  primaryClientOptions,
  hasCtResident,
  householdId,
}: Props) {
  const { control } = useFormContext<RecordExemptionUsedForm>();

  return (
    <Stack flex={1} sx={{ minHeight: 450 }}>
      <FormLayoutRow>
        <FormLayoutItem width={6}>
          <PrimaryClientDropdownSingle<RecordExemptionUsedForm>
            control={control}
            namespace={NAMESPACE}
            options={primaryClientOptions}
            labelOverride="Client"
            required
            hideGrantorContextualHelp
          />
        </FormLayoutItem>
        <FormLayoutItem width={6}>
          <FormAwareDatePickerInput<RecordExemptionUsedForm>
            control={control}
            label="As of date"
            required
            fieldName={
              `${NAMESPACE}.asOfDate` as const satisfies RecordExemptionUsedFormPaths
            }
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem width={6}>
          <FormAwareCurrencyInput<RecordExemptionUsedForm>
            isDecimalJSInput
            control={control}
            label="Lifetime exemption adjustment"
            contextualHelp={<LifetimeExemptionAdjustment />}
            required
            fieldName={
              `${NAMESPACE}.additionalLifetimeExemptionApplied` as const satisfies RecordExemptionUsedFormPaths
            }
          />
        </FormLayoutItem>
        <FormLayoutItem width={6}>
          <FormAwareCurrencyInput<RecordExemptionUsedForm>
            isDecimalJSInput
            control={control}
            label="GST exemption adjustment"
            contextualHelp={<GSTExemptionAdjustment />}
            required
            fieldName={
              `${NAMESPACE}.additionalGstExemptionApplied` as const satisfies RecordExemptionUsedFormPaths
            }
          />
        </FormLayoutItem>
      </FormLayoutRow>
      {hasCtResident && (
        <FormLayoutRow>
          <FormLayoutItem width={6}>
            <FormAwareCurrencyInput<RecordExemptionUsedForm>
              isDecimalJSInput
              control={control}
              label="Connecticut exemption adjustment"
              fieldName={
                `${NAMESPACE}.ctExemptionAdjustment` as const satisfies RecordExemptionUsedFormPaths
              }
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareTextInput<RecordExemptionUsedForm>
            fieldName={
              `${NAMESPACE}.notes` as const satisfies RecordExemptionUsedFormPaths
            }
            label="Notes"
            control={control}
            multiline
            rows={2}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareRadioGroup<RecordExemptionUsedForm>
            control={control}
            fieldName={
              `${NAMESPACE}.assetType` as const satisfies RecordExemptionUsedFormPaths
            }
            row
            options={assetTypeOptions}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem>
          <Accordion variant="filled" title="Document upload">
            <FormLayoutRow>
              <FormLayoutItem>
                <MultiDocumentUploaderWithListV2
                  householdId={householdId}
                  variant="compact"
                  allowSelectExistingDocuments={true}
                />
              </FormLayoutItem>
            </FormLayoutRow>
          </Accordion>
        </FormLayoutItem>
      </FormLayoutRow>
    </Stack>
  );
}
